import { useEffect, useState } from "react"

import { useQueryExportReportDownload } from "api/reactQuery/queries/export"
import {
  ExportStatus,
  ExportType,
  IExportTaskResponse,
} from "api/reactQuery/queries/export.types"
import { AxiosResponse } from "axios"
import {
  cleanupOldExportTasks,
  ExportTask,
  filterTasksByStatus,
  filterTasksByClassId,
  getExportTasksByType,
  addOrUpdateExportTask,
  removeExportTask,
} from "utils/exportTasks"

interface UseExportTasksProps {
  exportType: ExportType
  classId?: number
  refreshInterval?: number
}

/**
 * Custom hook for managing export tasks
 * Handles sessionStorage, periodic refresh, and download logic
 */
export const useExportTasks = ({
  exportType,
  classId,
  refreshInterval = 5000,
}: UseExportTasksProps) => {
  const [exportTasks, setExportTasks] = useState<ExportTask[]>(() =>
    getExportTasksByType(exportType)
  )
  const [exportReportIdToDownload, setExportReportIdToDownload] = useState<
    number | null
  >(null)

  useEffect(() => {
    cleanupOldExportTasks()
  }, [])

  const refreshTasks = () => {
    const refreshedTasks = getExportTasksByType(exportType)
    setExportTasks(refreshedTasks)
  }

  useEffect(() => {
    const interval = setInterval(() => {
      refreshTasks()
    }, refreshInterval)

    return () => clearInterval(interval)
  }, [exportType, refreshInterval])

  useEffect(() => {
    const handleTaskUpdate = () => {
      refreshTasks()
    }

    window.addEventListener("exportTaskAdded", handleTaskUpdate)

    return () => {
      window.removeEventListener("exportTaskAdded", handleTaskUpdate)
    }
  }, [exportType])

  useQueryExportReportDownload({
    exportReportId: exportReportIdToDownload ?? 0,
    options: {
      enabled: exportReportIdToDownload !== null,
      onSuccess: (response) => {
        const url = response.data.download_url
        if (url) {
          window.open(url, "_blank", "noopener,noreferrer")
        }
        setExportReportIdToDownload(null)
      },
    },
  })

  const handleExportSuccess = (
    response: AxiosResponse<IExportTaskResponse>,
    taskClassId?: number
  ) => {
    const { id, filename, export_type, created_at, status } = response.data

    const taskData: ExportTask = {
      id,
      filename,
      created_at,
      status,
      download_url: null,
      classId: taskClassId,
    }

    addOrUpdateExportTask(export_type, taskData)

    if (export_type === exportType) {
      refreshTasks()
    }
  }

  const removeTask = (taskId: number) => {
    removeExportTask(exportType, taskId)
    refreshTasks()
  }

  const allSuccessTasks = filterTasksByStatus(exportTasks, ExportStatus.SUCCESS)
  const successTasks = filterTasksByClassId(allSuccessTasks, classId)

  const allPendingTasks = filterTasksByStatus(exportTasks, ExportStatus.PENDING)
  const pendingTasks = filterTasksByClassId(allPendingTasks, classId)

  return {
    exportTasks,
    successTasks,
    pendingTasks,
    exportReportIdToDownload,
    setExportReportIdToDownload,
    refreshTasks,
    handleExportSuccess,
    removeTask,
  }
}
